import {
	BillingStatus,
	Layout,
	Marketing,
	Modal as ModalModule,
	UI,
	Utils,
} from 'common';
import AccountVerificationModal from 'common/src/code/accountVerification/AccountVerificationModal.component';
import differenceBy from 'lodash/differenceBy';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import uniqBy from 'lodash/uniqBy';
import React, { Component } from 'react';
import ContactsGrid from './ContactsGrid.component';
import ContactsLeftMenu from './ContactsLeftMenu.component';
import ContactsToolbar from './ContactsToolbar.component';
import ImportContactsModal from './importContacts/ImportContactsModal.component';
import MultiContactEditView from './MultiContactEditView.component';
import SingleContactEditView from './SingleContactEditView.component';
import ActiveItemMenuId from './utils/ActiveItemMenuId.enum';
import Contact from './utils/Contact';
import ContactsCommunication from './utils/ContactsCommunication';
import MailContact from './utils/MailContact';
import PageNamesEnum from './utils/PageNames.enum';

const { RegionType, CollapsibleSidePanel, CenterPanel } = Layout;
const { Spinner } = UI;
const { withLayoutAndModalContexts, Session, Logger, Navigation, Analytics } =
	Utils;
const { Modal, ModalButtonActionType } = ModalModule;
const { MarketingCampaign } = Marketing;
const { BillingStatusModal } = BillingStatus;

/**
 * Removes all non-digit characters and removes explicit US country code '1'
 */
const stripPhoneUsCountryCode = phone => {
	if (!phone) return phone;
	const digits = phone?.replace(/[^0-9]/g, '');
	return digits?.replace(/^1(\d{10})$/, '$1');
};

class ContactsMain extends Component {
	constructor(props) {
		super(props);

		const { customerId, accountStatus } = Session.getAccountInfo();
		let showEmailVerification = false;

		if (accountStatus === 'accountVerificationRequired') {
			showEmailVerification = true;
		}

		this.state = {
			contacts: null,
			selectedContacts: [],
			groups: [],
			costCodes: [],
			pageNumber: 1,
			pageSize: 100,
			totalCount: 0,
			sortedDesc: true,
			sortedBy: 'FirstName',
			mode: 'server',
			isAddingNewContact: false,
			refreshGroupUpdate: false,
			activeMenuItem: {
				activeMenuItemId: ActiveItemMenuId.All,
				activeMenuItemValue: null,
				leftMenuTitle: 'All Contacts',
			},
			counts: {
				all: 0,
				search: 0,
				selected: 0,
			},
			localStorageKey: `contacts_selection_${customerId || ''}`,
			isLoading: false,
			selectedContactsByFilter: {},
			loadingText: '',
			showBillingStatus: Session.showBillingStatusModal(),
			showEmailVerification,
		};

		this.gridApi = null;
	}

	componentDidMount() {
		const { showBillingStatus, showEmailVerification } = this.state;
		const {
			modalContext: { showModal },
			ldClient,
		} = this.props;

		if (ldClient) {
			Session.setFeatureFlags(ldClient.allFlags());
			const accountInfo = Session.getAccountInfo();
			Analytics.initLogRocket(ldClient, accountInfo);
		}

		if (showBillingStatus) {
			showModal({ component: BillingStatusModal });
		}

		if (showEmailVerification) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.accountVerificationCallback,
					emailOnly: true,
				},
			});
		}
	}

	/**
	 * Makes API call to get contacts, cost codes, and groups for a user.
	 * @param {Object} params Changes to active menu.
	 */
	fetchContacts = async (params = {}, callback) => {
		const {
			modalContext: { showError },
		} = this.props;

		// show the loading mask
		this.gridApi.showLoadingOverlay();

		// build the params and call the endpoint
		const fetchContactsPayload = this.buildFetchContactsPayload(params);
		const response =
			await ContactsCommunication.searchContacts(fetchContactsPayload);

		if (ContactsCommunication.isSuccessful(response)) {
			// set contacts in the state object
			this.setContacts(response.data, params, () => {
				const { TotalCount } = response.data;

				if (TotalCount === 0) {
					// show no data overlay
					this.gridApi.showNoRowsOverlay();
				} else {
					// hide loading spinner
					this.gridApi.hideOverlay();
				}

				// set selection based on selectedContacts array
				this.updateRowSelection();

				if (callback) {
					callback();
				}
			});
		} else {
			this.gridApi.hideOverlay();
			showError({ response });
		}
	};

	buildFetchContactsPayload = params => {
		const {
			activeMenuItem: { activeMenuItemId, activeMenuItemValue },
			pageSize,
			pageNumber,
			sortedBy,
			sortedDesc,
		} = this.state;

		const payload = {
			SortedBy: params.SortedBy || sortedBy,
			SortedDesc: isBoolean(params.SortedDesc) ? params.SortedDesc : sortedDesc,
			PageSize: params.PageSize || pageSize,
			PageNumber: params.PageNumber || pageNumber,
		};

		if (activeMenuItemId === ActiveItemMenuId.Search) {
			payload.SearchString = activeMenuItemValue;
		} else if (activeMenuItemId === ActiveItemMenuId.Groups) {
			payload.GroupId = activeMenuItemValue;
		} else if (activeMenuItemId === ActiveItemMenuId.CostCodes) {
			payload.CostCodeId = activeMenuItemValue;
		}

		return payload;
	};

	// sets user contact data in state
	setContacts = (data, params, callback) => {
		const {
			Contacts,
			Groups,
			CostCodes,
			PageNumber,
			PageSize,
			TotalContacts,
			TotalCount,
		} = data;
		const {
			activeMenuItem,
			selectedContacts,
			sortedBy,
			sortedDesc,
			costCodes,
			groups,
		} = this.state;
		const activeMenuItemId =
			params.activeMenuItemId || activeMenuItem.activeMenuItemId;

		this.setState(
			{
				contacts:
					activeMenuItemId === ActiveItemMenuId.Selected
						? selectedContacts
						: Contacts,
				groups:
					activeMenuItemId === ActiveItemMenuId.Groups ||
					activeMenuItemId === ActiveItemMenuId.All
						? Groups
						: groups,
				costCodes:
					activeMenuItemId === ActiveItemMenuId.CostCodes ||
					activeMenuItemId === ActiveItemMenuId.All
						? CostCodes
						: costCodes,
				pageNumber: PageNumber,
				pageSize: PageSize,
				totalCount: TotalCount,
				counts: {
					all: TotalContacts,
					search: activeMenuItemId === ActiveItemMenuId.Search ? TotalCount : 0,
					selected: selectedContacts.length,
				},
				sortedBy: params.SortedBy || sortedBy,
				sortedDesc: params.SortedDesc || sortedDesc,
				mode: 'server',
			},
			callback
		);
	};

	// sets previously selected contacts to be in checked state
	updateRowSelection = () => {
		const { selectedContacts } = this.state;

		if (selectedContacts.length > 0) {
			this.gridApi.forEachNode(node => {
				const isSelected = true;
				const clearSelection = false;
				const suppressFinishActions = true; // suppress 'SelectionChanged' event

				selectedContacts.forEach(contact => {
					if (node.data.ContactId === contact.ContactId) {
						// select the node
						node.setSelected(isSelected, clearSelection, suppressFinishActions);
					}
				});
			});
		}
	};

	// handles setting contacts from local storage when changing pages
	setContactsFromLocalStorage = ({ rowData, totalCount, pageSize }) => {
		this.setState(
			{
				contacts: rowData,
				totalCount,
				pageSize,
			},
			() => {
				if (rowData.length > 0) {
					this.gridApi.hideOverlay();
				} else {
					this.gridApi.showNoRowsOverlay();
				}
			}
		);
	};

	setSelectedContacts = selectedContacts => {
		const {
			counts,
			contacts,
			isAddingNewContact,
			selectedContactsByFilter,
			activeMenuItem,
		} = this.state;

		const updatedSelectedContacts = { ...selectedContactsByFilter };
		const leftFilterKey = `${activeMenuItem.activeMenuItemId}_${activeMenuItem.activeMenuItemValue}`;

		if (!isEmpty(updatedSelectedContacts)) {
			if (isEmpty(updatedSelectedContacts[leftFilterKey])) {
				updatedSelectedContacts[leftFilterKey] = [];
			}

			const deselectedContacts = differenceBy(
				updatedSelectedContacts[leftFilterKey],
				selectedContacts,
				'ContactId'
			);
			const deselectedContactsIds = deselectedContacts.map(
				contact => contact.ContactId
			);

			for (const [filterId, previousSelection] of Object.entries(
				updatedSelectedContacts
			)) {
				let updatedSelection = [...previousSelection];

				if (leftFilterKey === filterId) {
					const contactsToAdd = differenceBy(
						selectedContacts,
						updatedSelection,
						'ContactId'
					);
					updatedSelection = updatedSelection.concat(contactsToAdd);
				}

				updatedSelectedContacts[filterId] = updatedSelection.filter(
					record => deselectedContactsIds.indexOf(record.ContactId) === -1
				);
			}
		} else {
			updatedSelectedContacts[leftFilterKey] = selectedContacts;
		}

		const allSelectedContacts = uniqBy(
			flatten(Object.values(updatedSelectedContacts)),
			'ContactId'
		);

		const selectedCount = isArray(allSelectedContacts)
			? allSelectedContacts.length
			: 0;

		let contactsToDisplay;
		if (isAddingNewContact) {
			contactsToDisplay = contacts.slice(1);
		} else if (activeMenuItem.activeMenuItemId === ActiveItemMenuId.Selected) {
			contactsToDisplay = allSelectedContacts;
		} else {
			contactsToDisplay = contacts;
		}

		this.setState(
			{
				contacts: contactsToDisplay,
				selectedContacts: selectedCount === 0 ? [] : allSelectedContacts,
				isAddingNewContact: false,
				counts: {
					...counts,
					selected: selectedCount,
				},
				selectedContactsByFilter: updatedSelectedContacts,
			},
			() => {
				this.updateRowSelection();
				this.hideEastPanel(selectedCount === 0);
			}
		);
	};

	/**
	 * Sets the left menu item based on user selection
	 * @param {object} activeMenuItem
	 */
	onMenuItemClick = activeMenuItem => {
		const { isModal } = this.props;
		const { selectedContacts, isAddingNewContact, selectedContactsByFilter } =
			this.state;

		if (activeMenuItem.activeMenuItemId === ActiveItemMenuId.Selected) {
			this.setState(
				{
					contacts: isModal
						? selectedContacts
						: uniqBy(
								flatten(Object.values(selectedContactsByFilter)),
								'ContactId'
							),
					mode: isModal ? 'selected' : 'localStorage',
					isAddingNewContact: false,
					activeMenuItem,
				},
				() => {
					this.gridApi.selectAll();
				}
			);
		} else {
			this.setState(
				{
					mode: 'server',
					isAddingNewContact: false,
					selectedContacts: isAddingNewContact ? [] : selectedContacts,
					activeMenuItem,
				},
				() => {
					if (selectedContacts.length === 0) {
						this.hideEastPanel(true);
					}

					// build params for search endpoint
					this.fetchContacts();
				}
			);
		}
	};

	// resets selected contacts
	clearSelectedContacts = () => {
		this.setState(
			{
				selectedContacts: [],
				selectedContactsByFilter: {},
			},
			() => {
				this.gridApi.deselectAll();

				this.hideEastPanel(true);

				// fetch contacts data
				this.fetchContacts();
			}
		);
	};

	/**
	 * Updates the contacts with changed contacts properties
	 * @param {Object} data The new changed properties
	 */
	updateContactsState = (data, isContactData) => {
		const { selectedContacts, contacts, refreshGroupUpdate } = this.state;
		const selectedContactsIds = [];

		if (data.refreshGroupUpdate) {
			/* The actual state change happens in SingleContactEditView we are just triggering the ComponentDidUpdate */
			this.setState(
				{ refreshGroupUpdate: !refreshGroupUpdate },
				this.fetchContacts
			);
		} else if (isContactData) {
			this.setState(
				{
					selectedContacts: selectedContacts.map(contact => {
						selectedContactsIds.push(contact.ContactId);
						return { ...contact, ...data };
					}),
					contacts: (contacts || []).map(contact => {
						let updatedContact = contact;
						if (selectedContactsIds.includes(contact.ContactId)) {
							updatedContact = { ...contact, ...data };
						}
						return updatedContact;
					}),
				},
				this.updateRowSelection
			);
		} else {
			this.setState({
				...data,
			});
		}
	};

	onGridReady = ({ api }) => {
		// store the reference of the api locally
		this.gridApi = api;

		// call search endpoint
		this.fetchContacts();
	};

	// used when clicked select button when using Search Contacts modal in Mail app
	onPrimaryButtonClick = () => {
		const { selectedContacts } = this.state;
		const { onSuccessCallback } = this.props;

		if (isFunction(onSuccessCallback)) {
			onSuccessCallback(selectedContacts);
		}
	};

	// sets east panel visibility
	hideEastPanel = hidden => {
		const { layoutContext } = this.props;
		layoutContext.setHidden(RegionType.East, hidden);
	};

	onClickAdd = () => {
		const { activeMenuItem, isAddingNewContact, localStorageKey } = this.state;

		if (isAddingNewContact) {
			return;
		}

		// when adding a contact filter view is changed to 'All Contacts'
		if (activeMenuItem.activeMenuItemId !== ActiveItemMenuId.All) {
			this.setState(
				{
					mode: 'server',
					activeMenuItem: {
						activeMenuItemId: ActiveItemMenuId.All,
						activeMenuItemValue: null,
						leftMenuTitle: 'All Contacts',
					},
				},
				() => {
					this.fetchContacts({}, this.appendNewContact);
				}
			);
		} else {
			this.appendNewContact();
		}

		localStorage.removeItem(localStorageKey);
	};

	appendNewContact = () => {
		const { contacts, counts, localStorageKey } = this.state;
		const tempContact = Contact();

		this.setState(
			{
				isAddingNewContact: true,
				contacts: [tempContact, ...(contacts || [])],
				selectedContacts: [tempContact],
				counts: {
					...counts,
					selected: 0,
				},
			},
			() => {
				this.updateRowSelection();
				this.hideEastPanel(false);
			}
		);

		// set new created contact into localStorage
		localStorage.setItem(localStorageKey, JSON.stringify([tempContact]));
	};

	/**
	 * Calls addContact endpoint to add a new contact
	 * @param {Contact} contactToSave
	 */
	addContact = async contactToSave => {
		const { contacts, localStorageKey, counts } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		contactToSave.Phone = stripPhoneUsCountryCode(contactToSave.Phone);

		const response = await ContactsCommunication.addContact(contactToSave);

		if (ContactsCommunication.isSuccessful(response)) {
			const { ContactId } = response.data;
			contactToSave.ContactId = ContactId;

			Analytics.trackEvent(
				'addContact',
				{
					count: 1,
					source: 'manual',
					success: true,
				},
				Analytics.agent.segment
			);

			this.setState(
				{
					isAddingNewContact: false,
					contacts: [contactToSave, ...(contacts || []).slice(1)],
					selectedContacts: [contactToSave],
					counts: {
						...counts,
						all: counts.all + 1,
						selected: 1,
					},
				},
				this.updateRowSelection
			);

			// set new created contact into localStorage
			localStorage.setItem(localStorageKey, JSON.stringify([contactToSave]));

			return contactToSave;
		} else {
			Analytics.trackEvent(
				'addContact',
				{
					count: 1,
					source: 'manual',
					success: false,
					error: response,
				},
				Analytics.agent.segment
			);
			showError({ response });
		}
	};

	/**
	 * Calls updateContact endpoint to update existing contact
	 * @param {Contact} contactToSave
	 */
	updateContact = async (contactToSave, changedFields) => {
		const { contacts, selectedContacts, localStorageKey } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		contactToSave.Phone = stripPhoneUsCountryCode(contactToSave.Phone);

		const response = await ContactsCommunication.updateContact(contactToSave);

		if (ContactsCommunication.isSuccessful(response)) {
			const newSelectedContacts = selectedContacts.map(contact =>
				contact.ContactId === contactToSave.ContactId
					? Object.assign(contact, contactToSave)
					: contact
			);

			this.setState(
				{
					contacts: contacts.map(contact => {
						if (contact.ContactId === contactToSave.ContactId) {
							return Contact(contactToSave);
						}

						return contact;
					}),
					selectedContacts: newSelectedContacts,
					isAddingNewContact: false,
				},
				this.updateRowSelection
			);

			// set new created contact into localStorage
			localStorage.setItem(
				localStorageKey,
				JSON.stringify([...newSelectedContacts])
			);
		} else {
			showError({ response });
		}

		Logger.logToServer({
			category: 'edit_contact_details',
			edit_field: changedFields,
		});
	};

	getCostCodes = newCostCodeData => {
		const { costCodes } = this.state;
		const costCodeOptions = [];

		// restructure new cost codes keys to be consistent across Contacts app
		newCostCodeData.forEach(costCodeResponse => {
			costCodeOptions.push({
				id: costCodeResponse.ID,
				name: costCodeResponse.Code,
				count: 0,
				checkState: 0,
			});
		});
		costCodeOptions.push({ id: -1, name: 'None', count: 0, checkState: 0 });

		// associate new cost codes with count
		costCodeOptions.forEach(costCodeOption => {
			costCodes.forEach(costCode => {
				if (costCode.id === costCodeOption.id) {
					costCodeOption.count = costCode.count;
					costCodeOption.checkState = costCode.checkState;
				}
			});
		});

		this.setState({
			costCodes: costCodeOptions,
		});
	};

	deleteContacts = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { selectedContacts } = this.state;

		const contactIds = selectedContacts.map(contact => contact.ContactId);

		this.setState({ isLoading: true, loadingText: 'Deleting Contacts...' });

		const response = await ContactsCommunication.deleteContacts(contactIds);

		if (ContactsCommunication.isSuccessful(response)) {
			this.setState({ isLoading: false }, this.clearSelectedContacts);
		} else {
			this.setState({ isLoading: false }, showError({ response }));
		}
	};

	/**
	 * Makes API call to import contacts for a user.
	 * @param {Object} params request data.
	 */
	importContacts = async params => {
		const {
			modalContext: { showError },
		} = this.props;
		const { DuplicateHandle } = params;

		this.setState({ isLoading: true, loadingText: 'Importing Contacts...' });
		const response = await ContactsCommunication.importContact(params);

		if (ContactsCommunication.isSuccessful(response)) {
			if (DuplicateHandle === 0 && response.data) {
				this.processDuplicatesAndErrorsImports(params, response.data);
			} else {
				Analytics.trackEvent(
					'addContact',
					{
						count: response.data.Success.Imported,
						source: 'csv',
						success: true,
					},
					Analytics.agent.segment
				);

				this.setState({ isLoading: false }, this.fetchContacts);
			}
		} else {
			Analytics.trackEvent(
				'addContact',
				{
					count: response.ErrorRows,
					source: 'csv',
					success: false,
					error: response,
				},
				Analytics.agent.segment
			);

			this.setState({ isLoading: false }, () => showError({ response }));
		}
	};

	onPrintPostageClick = async () => {
		const { selectedContacts } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		// map properties for Mail app to consume
		const mappedContacts = selectedContacts.map(contact =>
			MailContact(contact)
		);

		// verify customs form is not needed
		let isEligibleForBatchPrint = false;

		if (selectedContacts.length === 1) {
			const contact = selectedContacts[0];
			if (
				this.isContactEligibleForBatchPrint(contact.CountryCode, contact.State)
			) {
				isEligibleForBatchPrint = true;
			}
		} else {
			selectedContacts.forEach(contact => {
				if (
					this.isContactEligibleForBatchPrint(
						contact.CountryCode,
						contact.State
					)
				) {
					isEligibleForBatchPrint = true;
				}
			});
		}

		if (isEligibleForBatchPrint) {
			const response =
				await ContactsCommunication.saveSelectedContacts(mappedContacts);

			if (ContactsCommunication.isSuccessful(response)) {
				Navigation.navigateTo('mail');
			} else {
				showError({ response });
			}
		} else {
			showError({
				data: {
					title: 'Invalid Contact Selected',
					description: `<p>Contacts with an address that requires a customs form cannot be batch printed.</p>
					<p>These include international prints and prints in the following states: AA, AE, AP, AS, FM, GU, MH, MP, PW. </p>
					<p>Remove those addresses to continue batch printing.</p>`,
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
			});

			Logger.logToServer({
				category: 'choose_contact_error',
				method: 'contacts_print_button',
				error_string: 'Invalid Contact Selected',
			});
		}
	};

	isContactEligibleForBatchPrint = (countryCode, state) =>
		this.isDomestic(countryCode) && !this.isAPO(state);

	isDomestic = countryCode =>
		countryCode.toUpperCase().search(/AS|GU|MH|MP|PW|PR|VI|US/) !== -1;

	isAPO = state =>
		state.toUpperCase().search(/AA|AE|AP|AS|FM|GU|MH|MP|PW/) !== -1;

	/**
	 * This is to process the Duplicates and Errors in the contacts imports.
	 * @param {Object} importsResponseData response data.
	 */
	processDuplicatesAndErrorsImports(params, importsResponseData) {
		const {
			modalContext: { showModal },
		} = this.props;
		const { DuplicateRows, ErrorRows } = importsResponseData;

		const isDuplicates =
			isArray(DuplicateRows) && !isEmpty(DuplicateRows)
				? PageNamesEnum.CheckDuplicates
				: 0;

		const isErrors =
			isArray(ErrorRows) && !isEmpty(ErrorRows) ? PageNamesEnum.CheckErrors : 0;

		const openPage = isDuplicates || isErrors;

		this.setState(
			{ isLoading: false },
			openPage > 0
				? () =>
						showModal({
							component: ImportContactsModal,
							data: {
								openPage,
								params,
								duplicatesAndErrors: importsResponseData,
								importContacts: this.importContacts,
							},
						})
				: this.fetchContacts
		);
	}

	render() {
		const {
			counts,
			contacts,
			costCodes,
			groups,
			pageNumber,
			pageSize,
			selectedContacts,
			totalCount,
			mode,
			activeMenuItem,
			localStorageKey,
			isAddingNewContact,
			refreshGroupUpdate,
			isLoading,
			loadingText,
		} = this.state;
		const { isModal, modalId } = this.props;

		const primaryButton = {
			type: ModalButtonActionType.Ok,
			text: `Select ${selectedContacts.length > 0 ? `(${selectedContacts.length})` : ''}`,
			disabled: selectedContacts.length < 1,
			onClick: this.onPrimaryButtonClick,
			dataqa: 'contacts-select-button',
		};

		const secondaryButton = {
			type: ModalButtonActionType.Cancel,
			text: 'Cancel',
			dataqa: 'contacts-cancel-button',
		};

		if (isModal) {
			return (
				<Modal
					modalId={modalId}
					className="contacts-main-contacts-modal"
					header="Search Contacts"
					primaryButton={primaryButton}
					secondaryButton={secondaryButton}
				>
					<div className="p-grid p-nogutter">
						{/* west region */}
						<MarketingCampaign />
						<ContactsLeftMenu
							isModal={isModal}
							counts={counts}
							groups={groups}
							costCodes={costCodes}
							activeMenuItem={activeMenuItem}
							fetchContacts={this.fetchContacts}
							getCostCodes={this.getCostCodes}
							onMenuItemClick={this.onMenuItemClick}
							updateContactsState={this.updateContactsState}
						/>

						{/* center */}
						<ContactsGrid
							isModal={isModal}
							contacts={contacts}
							totalCount={totalCount}
							pageSize={pageSize}
							pageNumber={pageNumber}
							gridApi={this.gridApi}
							activeMenuItemId={activeMenuItem.activeMenuItemId}
							onGridReady={this.onGridReady}
							fetchContacts={this.fetchContacts}
							setSelectedContacts={this.setSelectedContacts}
						/>
					</div>
				</Modal>
			);
		} else {
			return (
				<>
					{/* west region */}
					<CollapsibleSidePanel
						region={RegionType.West}
						title={activeMenuItem.leftMenuTitle}
					>
						<ContactsLeftMenu
							counts={counts}
							groups={groups}
							costCodes={costCodes}
							activeMenuItem={activeMenuItem}
							fetchContacts={this.fetchContacts}
							getCostCodes={this.getCostCodes}
							onMenuItemClick={this.onMenuItemClick}
							updateContactsState={this.updateContactsState}
						/>
					</CollapsibleSidePanel>

					{/* center */}
					<CenterPanel>
						<div className="center-panel-wrapper">
							<MarketingCampaign />
							<ContactsToolbar
								groups={groups}
								costCodes={costCodes}
								selectedContacts={selectedContacts}
								isAddingNewContact={isAddingNewContact}
								onClickAdd={this.onClickAdd}
								getCostCodes={this.getCostCodes}
								fetchContacts={this.fetchContacts}
								deleteContacts={this.deleteContacts}
								importContacts={this.importContacts}
								onPrintPostageClick={this.onPrintPostageClick}
								updateContactsState={this.updateContactsState}
							/>
							<ContactsGrid
								mode={mode}
								contacts={contacts}
								totalCount={totalCount}
								pageSize={pageSize}
								pageNumber={pageNumber}
								gridApi={this.gridApi}
								localStorageKey={localStorageKey}
								activeMenuItemId={activeMenuItem.activeMenuItemId}
								onGridReady={this.onGridReady}
								fetchContacts={this.fetchContacts}
								setSelectedContacts={this.setSelectedContacts}
								setContactsFromLocalStorage={this.setContactsFromLocalStorage}
							/>
						</div>
					</CenterPanel>

					{/* east region */}
					<CollapsibleSidePanel
						region={RegionType.East}
						title="Contact Details"
						hideCollapsibleButton
					>
						<div className="contacts-details">
							{selectedContacts.length === 1 ? (
								<SingleContactEditView
									groups={groups}
									costCodes={costCodes}
									contact={selectedContacts[0]}
									selectedContacts={selectedContacts}
									refreshGroupUpdate={refreshGroupUpdate}
									addContact={this.addContact}
									getCostCodes={this.getCostCodes}
									fetchContacts={this.fetchContacts}
									updateContact={this.updateContact}
									deleteContacts={this.deleteContacts}
									onPrintPostageClick={this.onPrintPostageClick}
								/>
							) : (
								<MultiContactEditView
									selectedContacts={selectedContacts}
									deleteContacts={this.deleteContacts}
									onPrintPostageClick={this.onPrintPostageClick}
									clearSelectedContacts={this.clearSelectedContacts}
								/>
							)}
						</div>
					</CollapsibleSidePanel>
					{isLoading && <Spinner msg={loadingText} />}
				</>
			);
		}
	}
}

export default withLayoutAndModalContexts(ContactsMain);
